import type { PushContentInput } from '../../../api/client.ts';
import { readPath, removePath, writePath, type ChannelContent } from '../shared/localization.ts';

/**
 * The push composer's content model (BB-73): what it writes into the campaign channels `push_android` / `push_ios`.
 * Two modes — separate content per platform (Signal New: each channel its own content with one platform) or one
 * shared content with both platforms written into both channels; the delivery renders only its own platform, using
 * that platform's overrides. Empty texts are left out rather than stored as `''`: the server reads an empty title as
 * «only spaces».
 */
export type { PushContentInput };
export type PushPlatform = 'android' | 'ios';
export type PushChannel = 'push_android' | 'push_ios';
export type PushComposerMode = 'separate' | 'shared';
export interface PushComposerValue {
  push_android?: PushContentInput;
  push_ios?: PushContentInput;
}
export type PushAction = NonNullable<PushContentInput['action']>;
export type PushButtonAction = PushAction | { kind: 'dismiss' };
export type PushButton = NonNullable<PushContentInput['androidButtons']>[number];
export type PushSettingsInput = NonNullable<PushContentInput['settings']>;

export const PLATFORMS: readonly PushPlatform[] = ['android', 'ios'];
export const PLATFORM_LABELS: Record<PushPlatform, string> = { android: 'Android', ios: 'iOS' };
export const PUSH_CHANNEL: Record<PushPlatform, PushChannel> = { android: 'push_android', ios: 'push_ios' };
export const MAX_ANDROID_BUTTONS = 3;

/** Send settings that only iOS reads; the rest is common or Android's. */
const IOS_SETTING_KEYS = ['iosDeviceFamily', 'iosInterruptionLevel', 'iosRelevanceScore', 'iosThreadId', 'iosBadge', 'sound'] as const;

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

/** `object` without `undefined` values and without objects left empty (overrides nobody filled in). */
function compact<T extends Record<string, unknown>>(object: T): T {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(object)) {
    if (value === undefined) continue;
    if (isObject(value) && key !== 'extras' && key !== 'settings' && Object.keys(value).length === 0) continue;
    out[key] = value;
  }
  return out as T;
}

export const emptyContent = (platforms: readonly PushPlatform[]): PushContentInput => ({ platforms: [...platforms], notificationType: 'standard', action: { kind: 'open_app' } });

/** Platforms the composer writes: the channels present, in order; both for a new message. */
export function platformsOf(value: PushComposerValue): PushPlatform[] {
  const present = PLATFORMS.filter((p) => value[PUSH_CHANNEL[p]]);
  return present.length ? present : [...PLATFORMS];
}

/** Shared content when both channels hold the same content written for both platforms. */
export function detectMode(value: PushComposerValue): PushComposerMode {
  const a = value.push_android;
  const i = value.push_ios;
  if (!a || !i) return a?.platforms?.length === 2 || i?.platforms?.length === 2 ? 'shared' : 'separate';
  return a === i || JSON.stringify(a) === JSON.stringify(i) ? 'shared' : 'separate';
}

/** The one content of shared mode. */
export const sharedContent = (value: PushComposerValue): PushContentInput => value.push_android ?? value.push_ios ?? emptyContent(PLATFORMS);

/** What a mode edits for one platform: the shared content, or the platform's own channel. */
export const contentFor = (value: PushComposerValue, mode: PushComposerMode, platform: PushPlatform): PushContentInput =>
  mode === 'shared' ? sharedContent(value) : (value[PUSH_CHANNEL[platform]] ?? emptyContent([platform]));

/** Shared mode: the same content, written for `platforms`, into each of their channels. */
export function writeShared(content: PushContentInput, platforms: readonly PushPlatform[]): PushComposerValue {
  const next = { ...content, platforms: [...platforms] };
  return Object.fromEntries(platforms.map((p) => [PUSH_CHANNEL[p], next]));
}

/** Separate mode: one platform's content into its channel, the other channel untouched. */
export const writeSeparate = (value: PushComposerValue, platform: PushPlatform, content: PushContentInput): PushComposerValue => ({ ...value, [PUSH_CHANNEL[platform]]: { ...content, platforms: [platform] } });

/**
 * A content written for one platform: that platform's overrides become the texts and click action, the other
 * platform's parts (overrides, buttons or category) go.
 */
export function forPlatform(content: PushContentInput, platform: PushPlatform): PushContentInput {
  const { androidOverrides, iosOverrides, actionOverrides, androidButtons, iosCategory, ...base } = content;
  if (platform === 'android') {
    return compact({
      ...base,
      platforms: ['android'],
      title: androidOverrides?.title ?? base.title,
      body: androidOverrides?.body ?? base.body,
      androidOverrides: androidOverrides?.summary ? { summary: androidOverrides.summary } : undefined,
      action: actionOverrides?.android ?? base.action,
      androidButtons,
    });
  }
  return compact({
    ...base,
    platforms: ['ios'],
    title: iosOverrides?.title ?? base.title,
    body: iosOverrides?.body ?? base.body,
    iosOverrides: iosOverrides?.subtitle ? { subtitle: iosOverrides.subtitle } : undefined,
    action: actionOverrides?.ios ?? base.action,
    iosCategory,
  });
}

/** What one platform shows of a content: its overrides laid over the common texts. */
export function effectiveContent(content: PushContentInput, platform: PushPlatform): { title?: string; subtitle?: string; body?: string; summary?: string; action: PushAction; imageUrl?: string } {
  const o = platform === 'android' ? content.androidOverrides : content.iosOverrides;
  return {
    title: o?.title ?? content.title,
    body: o?.body ?? content.body,
    ...(platform === 'ios' && content.iosOverrides?.subtitle ? { subtitle: content.iosOverrides.subtitle } : {}),
    ...(platform === 'android' && content.androidOverrides?.summary ? { summary: content.androidOverrides.summary } : {}),
    action: content.actionOverrides?.[platform] ?? content.action ?? { kind: 'open_app' },
    ...(content.media?.imageUrl ? { imageUrl: content.media.imageUrl } : {}),
  };
}

const sameJson = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
const differs = (text: unknown, base: unknown) => (typeof text === 'string' && text !== base ? text : undefined);

/** iOS translations laid into the Android ones as `iosOverrides` where their texts differ. */
function mergeLocalized(android: PushContentInput['localized'], ios: PushContentInput['localized']): PushContentInput['localized'] {
  if (!ios) return android;
  const out: Record<string, Record<string, unknown>> = { ...(android ?? {}) };
  for (const [locale, entry] of Object.entries(ios)) {
    const target = { ...(out[locale] ?? {}) };
    const overrides = compact({
      ...(isObject(target.iosOverrides) ? target.iosOverrides : {}),
      title: differs(entry.title, target.title),
      body: differs(entry.body, target.body),
      subtitle: isObject(entry.iosOverrides) ? (entry.iosOverrides.subtitle as string | undefined) : undefined,
    });
    if (Object.keys(overrides).length) target.iosOverrides = overrides;
    if (Object.keys(target).length) out[locale] = target;
  }
  return Object.keys(out).length ? out : undefined;
}

/**
 * Separate → shared: the Android content is the base; iOS texts, click action, category, settings and translations
 * that differ become iOS overrides, so nothing typed is lost.
 */
export function toShared(value: PushComposerValue): PushComposerValue {
  const a = value.push_android;
  const i = value.push_ios;
  if (!a || !i) {
    const only = a ?? i;
    return only ? writeShared(only, [a ? 'android' : 'ios']) : writeShared(emptyContent(PLATFORMS), PLATFORMS);
  }
  const iosSettings = Object.fromEntries(IOS_SETTING_KEYS.flatMap((k) => (i.settings?.[k] !== undefined ? [[k, i.settings[k]]] : [])));
  const androidSettings = Object.fromEntries(Object.entries(a.settings ?? {}).filter(([k]) => !(IOS_SETTING_KEYS as readonly string[]).includes(k)));
  const merged = compact({
    ...a,
    iosOverrides: compact({ title: differs(i.title, a.title), body: differs(i.body, a.body), subtitle: i.iosOverrides?.subtitle }),
    actionOverrides: sameJson(i.action, a.action) ? undefined : { ios: i.action ?? { kind: 'open_app' } },
    iosCategory: i.iosCategory,
    media: a.media ?? i.media,
    settings: Object.keys(androidSettings).length + Object.keys(iosSettings).length ? { ...androidSettings, ...iosSettings } : undefined,
    extras: Object.keys({ ...i.extras, ...a.extras }).length ? { ...(i.extras ?? {}), ...(a.extras ?? {}) } : undefined,
    localized: mergeLocalized(a.localized, i.localized),
    translationStatus: i.translationStatus || a.translationStatus ? { ...(i.translationStatus ?? {}), ...(a.translationStatus ?? {}) } : undefined,
  });
  return writeShared(merged, PLATFORMS);
}

/** Shared → separate: each platform gets the content with its overrides folded in. */
export function toSeparate(value: PushComposerValue): PushComposerValue {
  const base = sharedContent(value);
  const platforms = platformsOf(value);
  return Object.fromEntries(platforms.map((p) => [PUSH_CHANNEL[p], forPlatform(base, p)]));
}

/** Switches the mode, converting the contents. */
export const switchMode = (value: PushComposerValue, mode: PushComposerMode): PushComposerValue => (mode === 'shared' ? toShared(value) : toSeparate(value));

/** Adds or removes a platform (at least one stays); a platform added in separate mode starts from the other's texts. */
export function setPlatforms(value: PushComposerValue, mode: PushComposerMode, platforms: readonly PushPlatform[]): PushComposerValue {
  const wanted = PLATFORMS.filter((p) => platforms.includes(p));
  if (!wanted.length) return value;
  if (mode === 'shared') return writeShared(sharedContent(value), wanted);
  const next: PushComposerValue = {};
  for (const p of wanted) {
    const own = value[PUSH_CHANNEL[p]];
    const other = value[PUSH_CHANNEL[p === 'android' ? 'ios' : 'android']];
    next[PUSH_CHANNEL[p]] = own ?? (other ? forPlatform(other, p) : emptyContent([p]));
  }
  return next;
}

/** A text field set, or left out when empty (with any object it leaves empty). */
export function setText(content: PushContentInput, path: string, text: string): PushContentInput {
  const object = content as ChannelContent;
  return (text === '' ? removePath(object, path) : writePath(object, path, text)) as PushContentInput;
}

export const textAt = (content: PushContentInput, path: string): string => {
  const v = readPath(content as ChannelContent, path);
  return typeof v === 'string' ? v : '';
};

/** `content.settings` with one field set, or left out when `undefined`. */
export function setSetting<K extends keyof PushSettingsInput>(content: PushContentInput, key: K, value: PushSettingsInput[K] | undefined): PushContentInput {
  const settings: Record<string, unknown> = { ...(content.settings ?? {}) };
  if (value === undefined) delete settings[key];
  else settings[key] = value;
  return { ...content, settings: settings as PushSettingsInput };
}

/** A button id not used yet: `button_1`, `button_2`, … */
export function nextButtonId(buttons: readonly PushButton[]): string {
  for (let n = 1; ; n++) if (!buttons.some((b) => b.id === `button_${n}`)) return `button_${n}`;
}
